import * as THREE from 'three'
import type { MarginType } from './materials'
import { TOOTH_WIDTH_MM } from './resistance'

export type ProfilePoint = { r: number; z: number }

export type FinishLineGeometry = {
  marginType: MarginType
  terminalPoint: ProfilePoint
  points: ProfilePoint[]
  wallStartPoint: ProfilePoint
  depthMm: number
  widthMm: number
}

export type MolarProfile = {
  points: ProfilePoint[]
  surfacePoints: ProfilePoint[]
  finishLine: FinishLineGeometry
  heightMm: number
  baseRadiusMm: number
  topRadiusMm: number
  capHeightMm: number
  wallBaseHeightMm: number
  wallBaseRadiusMm: number
}

export type CrownProfile = {
  points: ProfilePoint[]
  innerPoints: ProfilePoint[]
  outerPoints: ProfilePoint[]
  marginPoint: ProfilePoint
}

const CAP_HEIGHT_MM = 0.2
const DEFAULT_AXIAL_REDUCTION_MM = 0.65

export function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

function createFinishLine(
  baseRadiusMm: number,
  marginType: MarginType,
  axialReductionMm: number,
): FinishLineGeometry {
  const widthMm = Math.max(0.2, axialReductionMm)
  const terminalPoint = { r: baseRadiusMm + widthMm, z: 0 }

  if (marginType === 'shoulder') {
    const wallStartPoint = { r: baseRadiusMm, z: 0 }
    return {
      marginType,
      terminalPoint,
      points: [terminalPoint, wallStartPoint],
      wallStartPoint,
      depthMm: 0,
      widthMm,
    }
  }

  const depthMm = marginType === 'deep-chamfer'
    ? Math.min(0.48, Math.max(0.28, widthMm * 0.42))
    : Math.min(0.28, Math.max(0.16, widthMm * 0.3))
  const samples = marginType === 'deep-chamfer' ? 8 : 6
  const points: ProfilePoint[] = []

  for (let index = 0; index <= samples; index += 1) {
    const theta = (index / samples) * Math.PI / 2
    points.push({
      r: baseRadiusMm + widthMm * (1 - Math.sin(theta)),
      z: depthMm * (1 - Math.cos(theta)),
    })
  }

  const wallStartPoint = points.at(-1)!
  return { marginType, terminalPoint, points, wallStartPoint, depthMm, widthMm }
}

/** Shared section for the prepared tooth and the crown fitting surface. */
export function calculateProfile(
  heightMm: number,
  tocDeg: number,
  marginType: MarginType = 'chamfer',
  axialReductionMm = DEFAULT_AXIAL_REDUCTION_MM,
): MolarProfile {
  const baseRadiusMm = TOOTH_WIDTH_MM / 2
  const finishLine = createFinishLine(baseRadiusMm, marginType, axialReductionMm)
  const wallBaseHeightMm = finishLine.wallStartPoint.z
  const axialWallHeightMm = Math.max(0.1, heightMm - wallBaseHeightMm)
  const topRadiusMm = Math.max(
    0.6,
    baseRadiusMm - axialWallHeightMm * Math.tan(toRadians(tocDeg / 2)),
  )
  const surfacePoints: ProfilePoint[] = [...finishLine.points]
  const wallSamples = 8
  const capSamples = 8

  for (let index = 1; index <= wallSamples; index += 1) {
    const progress = index / wallSamples
    surfacePoints.push({
      r: baseRadiusMm + (topRadiusMm - baseRadiusMm) * progress,
      z: wallBaseHeightMm + axialWallHeightMm * progress,
    })
  }

  for (let index = 1; index <= capSamples; index += 1) {
    const progress = index / capSamples
    surfacePoints.push({
      r: topRadiusMm * (1 - progress),
      z: heightMm + CAP_HEIGHT_MM * (2 * progress - progress ** 2),
    })
  }

  return {
    points: [{ r: 0, z: 0 }, ...surfacePoints],
    surfacePoints,
    finishLine,
    heightMm,
    baseRadiusMm,
    topRadiusMm,
    capHeightMm: CAP_HEIGHT_MM,
    wallBaseHeightMm,
    wallBaseRadiusMm: finishLine.wallStartPoint.r,
  }
}

export function profileToVectors(points: ProfilePoint[]): THREE.Vector2[] {
  return points.map(({ r, z }) => new THREE.Vector2(r, z))
}

export type CrownProfileOptions = {
  radialThicknessMm?: number
  occlusalThicknessMm?: number
}

/** Closed shell whose inner surface is exactly the prepared-tooth surface. */
export function calculateCrownProfile(
  profile: MolarProfile,
  options: CrownProfileOptions = {},
): CrownProfile {
  const radialThickness = Math.max(0.05, options.radialThicknessMm ?? DEFAULT_AXIAL_REDUCTION_MM)
  const occlusalThickness = Math.max(0.05, options.occlusalThicknessMm ?? 0.55)
  const marginPoint = { ...profile.finishLine.terminalPoint }
  const wallStartZ = profile.finishLine.wallStartPoint.z
  const outerPoints: ProfilePoint[] = [marginPoint]
  const wallSamples = 8
  const capSamples = 8

  const outerBaseRadius = profile.baseRadiusMm + radialThickness
  const outerTopRadius = profile.topRadiusMm + radialThickness
  for (let index = 1; index <= wallSamples; index += 1) {
    const progress = index / wallSamples
    outerPoints.push({
      r: outerBaseRadius + (outerTopRadius - outerBaseRadius) * progress,
      z: wallStartZ + (profile.heightMm - wallStartZ) * progress,
    })
  }

  for (let index = 1; index <= capSamples; index += 1) {
    const progress = index / capSamples
    outerPoints.push({
      r: outerTopRadius * (1 - progress),
      z: profile.heightMm
        + (profile.capHeightMm + occlusalThickness) * (2 * progress - progress ** 2),
    })
  }

  const innerPoints = profile.surfacePoints.map((point) => ({ ...point }))
  return {
    points: [...outerPoints, ...[...innerPoints].reverse()],
    innerPoints,
    outerPoints,
    marginPoint,
  }
}

/** Axial wall used to show the resistance area on the force-facing half. */
export function calculateResistanceWall(profile: MolarProfile): ProfilePoint[] {
  const inset = 0.035
  return [
    {
      r: profile.finishLine.wallStartPoint.r + inset,
      z: profile.finishLine.wallStartPoint.z + 0.04,
    },
    { r: profile.topRadiusMm + inset, z: profile.heightMm - 0.08 },
  ]
}
